<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>View Sales Report</h5>
                <p>Use View Report for on-screen data. Export creates Excel or PDF in the background with the selected filters.</p>
              </div>
              <div class="export-actions">
                <button
                  class="btn btn-outline-success"
                  @click="startExport('excel')"
                  :disabled="exportLoading"
                >
                  {{ exportLoading && exportJob.export_format === 'excel' ? 'Preparing...' : 'Export Excel' }}
                </button>
                <button
                  class="btn btn-outline-danger"
                  @click="startExport('pdf')"
                  :disabled="exportLoading"
                >
                  {{ exportLoading && exportJob.export_format === 'pdf' ? 'Preparing...' : 'Export PDF' }}
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div class="filter-grid">
                <div class="form-group">
                  <label>From Date</label>
                  <b-form-input v-model="filters.startdate" type="date" />
                </div>
                <div class="form-group">
                  <label>To Date</label>
                  <b-form-input v-model="filters.enddate" type="date" />
                </div>
                <div class="form-group">
                  <label>Category</label>
                  <b-form-select v-model="filters.categoryid" :options="categoryOptions" />
                </div>
                <div class="form-group">
                  <label>Report Type</label>
                  <b-form-select v-model="filters.groupBy" :options="groupByOptions" />
                </div>
                <div class="button-group">
                  <button class="btn btn-submit" @click="generateReport">View Report</button>
                  <button class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
                </div>
              </div>
            </div>

            <div v-if="exportJob.job_id" class="export-status" :class="`status-${exportJob.status}`">
              <div class="export-status-copy">
                <strong>{{ exportStatusTitle }}</strong>
                <span>{{ exportStatusText }}</span>
              </div>
              <button
                v-if="exportJob.status === 'completed'"
                class="btn btn-sm btn-download"
                @click="downloadExport"
              >
                Download {{ exportJob.export_format === "pdf" ? "PDF" : "Excel" }}
              </button>
            </div>

            <div class="summary-strip">
              <div>
                <span>Total Rows</span>
                <strong>{{ summaryRowCount }}</strong>
              </div>
              <div>
                <span>Total Qty</span>
                <strong>{{ formatNumber(summary.quantity) }}</strong>
              </div>
              <div>
                <span>Total Amount</span>
                <strong>{{ formatCurrency(summary.amount) }}</strong>
              </div>
              <div>
                <span>Total Tax</span>
                <strong>{{ formatCurrency(summary.taxamount) }}</strong>
              </div>
              <div>
                <span>Grand Total</span>
                <strong>{{ formatCurrency(summary.total) }}</strong>
              </div>
            </div>

            <div v-if="filters.groupBy === 'billwise'" class="billwise-sheet">
              <div v-if="loading" class="billwise-message">Loading bill-wise report...</div>
              <div v-else-if="!displayRows.length" class="billwise-message">No sales found for the selected period.</div>
              <section v-for="(bill, index) in displayRows" :key="bill.rowkey" class="billwise-bill">
                <header class="billwise-bill-header">
                  <span class="bill-index">{{ index + 1 }}</span>
                  <strong>{{ displayValue(bill.billno) }}</strong>
                  <strong class="bill-customer">{{ bill.customername || "Walk-in Customer" }}</strong>
                  <span v-if="bill.address" class="bill-address">Addr: {{ bill.address }}</span>
                </header>
                <table class="billwise-items">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Item Code</th>
                      <th>Item Desc</th>
                      <th class="numeric">Qty</th>
                      <th class="numeric">Rate</th>
                      <th class="numeric">Amount</th>
                      <th class="numeric">GST %</th>
                      <th class="numeric">GST Amt</th>
                      <th class="numeric">T.C. Amt</th>
                      <th class="numeric">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, itemIndex) in bill.itemlines" :key="`${bill.rowkey}-item-${itemIndex}`">
                      <td>{{ displayValue(item.billdate) }}</td>
                      <td>{{ displayValue(item.itemcode) }}</td>
                      <td>{{ displayValue(item.itemname) }}</td>
                      <td class="numeric">{{ formatNumber(item.quantity) }}</td>
                      <td class="numeric">{{ formatCurrency(item.saleprice) }}</td>
                      <td class="numeric">{{ formatCurrency(item.amount) }}</td>
                      <td class="numeric">{{ formatNumber(item.taxpercent) }}</td>
                      <td class="numeric">{{ formatCurrency(item.taxamount) }}</td>
                      <td class="numeric">-</td>
                      <td class="numeric">{{ formatCurrency(item.total) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="3">Sub Total :</td>
                      <td class="numeric">{{ formatNumber(bill.quantity) }}</td>
                      <td></td>
                      <td class="numeric">{{ formatCurrency(bill.amount) }}</td>
                      <td></td>
                      <td class="numeric">{{ formatCurrency(bill.taxamount) }}</td>
                      <td class="numeric">{{ formatCurrency(bill.roundoff) }}</td>
                      <td class="numeric">{{ formatCurrency(bill.total) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="displayRows"
                :loading="loading"
                :pagination="false"
                row-key="rowkey"
                :row-class-name="rowClassName"
              >
                <template #bodyCell="{ column, record, index }">
                  <template v-if="record.rowtype === 'category_header' && column.key === 'slno' && filters.groupBy === 'itemwise'">
                    <span class="group-header-text">{{ displayValue(record.itemname) }}</span>
                  </template>
                  <template v-else-if="column.key === 'slno'">
                    {{ rowSerialNumber(record, index) }}
                  </template>
                  <template v-else-if="record.rowtype === 'category_header' && ['categoryname', 'itemname'].includes(column.key)">
                    <span class="group-header-text">{{ displayValue(filters.groupBy === 'itemwise' ? '' : record.categoryname) }}</span>
                  </template>
                  <template v-else-if="record.rowtype === 'category_header'">
                    {{ "" }}
                  </template>
                  <template v-else-if="record.rowtype === 'category_total' && ['categoryname', 'itemname'].includes(column.key)">
                    <span class="group-total-text">{{ displayValue(record[column.dataIndex]) }}</span>
                  </template>
                  <template v-else-if="record.rowtype === 'category_total' && ['saleprice', 'taxpercent'].includes(column.key)">
                    {{ "" }}
                  </template>
                  <template v-else-if="moneyColumns.includes(column.key)">
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                  <template v-else-if="qtyColumns.includes(column.key)">
                    <span class="qty-text">{{ formatNumber(record[column.key]) }}</span>
                  </template>
                  <template v-else-if="column.key === 'categoryname'">
                    <span class="category-pill">{{ record.categoryname || "-" }}</span>
                  </template>
                  <template v-else>
                    {{ displayValue(record[column.dataIndex]) }}
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const itemwiseColumns = [
  { title: "Sl No", key: "slno", width: 70 },
  { title: "Date", dataIndex: "billdate", key: "billdate" },
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Item Code", dataIndex: "itemcode", key: "itemcode" },
  { title: "Item Desc", dataIndex: "itemname", key: "itemname" },
  { title: "Customer", dataIndex: "customername", key: "customername" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Sale Price", dataIndex: "saleprice", key: "saleprice", align: "right" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right" },
  { title: "GST %", dataIndex: "taxpercent", key: "taxpercent", align: "right" },
  { title: "GST Amt", dataIndex: "taxamount", key: "taxamount", align: "right" },
  { title: "T.C. Amt", dataIndex: "roundoff", key: "roundoff", align: "right" },
  { title: "Total", dataIndex: "total", key: "total", align: "right" },
];

const categorywiseColumns = [
  { title: "Sl No", key: "slno", width: 70 },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Date", dataIndex: "billdate", key: "billdate" },
  { title: "Item Code", dataIndex: "itemcode", key: "itemcode" },
  { title: "Item Desc", dataIndex: "itemname", key: "itemname" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Sale Price", dataIndex: "saleprice", key: "saleprice", align: "right" },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right" },
  { title: "GST %", dataIndex: "taxpercent", key: "taxpercent", align: "right" },
  { title: "GST Amt", dataIndex: "taxamount", key: "taxamount", align: "right" },
  { title: "Total", dataIndex: "total", key: "total", align: "right" },
];

export default {
  name: "salesreport",
  data() {
    return {
      title: "Sales Report",
      title1: "Billwise, itemwise, and categorywise sales",
      rawRows: [],
      categories: [],
      loading: false,
      filters: {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        categoryid: "",
        groupBy: "itemwise",
      },
      exportJob: {
        job_id: "",
        status: "",
        export_format: "",
        filename: "",
        error: "",
      },
      exportPoller: null,
      moneyColumns: ["saleprice", "amount", "taxamount", "roundoff", "total"],
      qtyColumns: ["quantity", "itemcount", "billcount"],
    };
  },
  computed: {
    categoryOptions() {
      return [
        { value: "", text: "All Categories" },
        ...this.categories.map((category) => ({
          value: category.categoryid,
          text: category.categoryname,
        })),
      ];
    },
    groupByOptions() {
      return [
        { value: "itemwise", text: "Item Wise" },
        { value: "billwise", text: "Bill Wise" },
        { value: "categorywise", text: "Category Wise" },
      ];
    },
    columns() {
      if (this.filters.groupBy === "categorywise") return categorywiseColumns;
      return itemwiseColumns;
    },
    displayRows() {
      if (this.filters.groupBy === "billwise") {
        const grouped = new Map();
        this.rawRows.forEach((row) => {
          const key = row.saleid || [row.billdate, row.billno, row.customername || ""].join("|");
          if (!grouped.has(key)) {
            grouped.set(key, {
              rowkey: key,
              billdate: row.billdate,
              billno: row.billno,
              customername: row.customername,
              address: row.address,
              itemcount: 0,
              itemlines: [],
              quantity: 0,
              amount: 0,
              taxamount: 0,
              roundoff: this.filters.categoryid ? 0 : Number(row.roundoff || 0),
              total: 0,
              netamount: row.netamount,
            });
          }
          const bill = grouped.get(key);
          bill.itemcount += 1;
          bill.itemlines.push({
            billdate: row.billdate,
            itemcode: row.itemcode,
            itemname: row.itemname,
            quantity: row.quantity,
            saleprice: row.saleprice,
            amount: row.amount,
            taxpercent: row.taxpercent,
            taxamount: row.taxamount,
            total: row.total,
          });
          bill.quantity += Number(row.quantity || 0);
          bill.amount += Number(row.amount || 0);
          bill.taxamount += Number(row.taxamount || 0);
        });

        return Array.from(grouped.values()).map((row) => {
          const calculatedTotal = row.amount + row.taxamount + row.roundoff;
          return {
            ...row,
            total: !this.filters.categoryid && row.netamount !== null && row.netamount !== undefined
              ? Number(row.netamount)
              : calculatedTotal,
          };
        });
      }
      if (this.filters.groupBy === "itemwise") {
        return this.buildItemRows(this.rawRows);
      }
      return this.buildCategoryRows(this.rawRows);
    },
    summaryRowCount() {
      if (this.filters.groupBy === "billwise") return this.displayRows.length;
      return this.displayRows.filter((row) => row.rowtype === "item" || !row.rowtype).length;
    },
    summary() {
      const rowsToSum = this.displayRows.filter(
        (row) => !row.rowtype || row.rowtype === "item" || this.filters.groupBy === "billwise"
      );
      return rowsToSum.reduce(
        (acc, row) => {
          acc.quantity += Number(row.quantity || 0);
          acc.amount += Number(row.amount || 0);
          acc.taxamount += Number(row.taxamount || 0);
          acc.total += Number(row.total || 0);
          return acc;
        },
        { quantity: 0, amount: 0, taxamount: 0, total: 0 }
      );
    },
    exportLoading() {
      return ["queued", "running"].includes(this.exportJob.status);
    },
    exportStatusTitle() {
      if (this.exportJob.status === "completed") return "Export ready";
      if (this.exportJob.status === "failed") return "Export failed";
      if (this.exportLoading) return "Export is being generated";
      return "Export";
    },
    exportStatusText() {
      if (this.exportJob.status === "completed") {
        return this.exportJob.filename || "Your file is ready to download.";
      }
      if (this.exportJob.status === "failed") {
        return this.exportJob.error || "Something went wrong while generating the file.";
      }
      if (this.exportLoading) {
        return `Preparing ${this.exportJob.export_format === "pdf" ? "PDF" : "Excel"} with the selected filters.`;
      }
      return "";
    },
  },
  created() {
    this.fetchCategories();
    this.generateReport();
  },
  beforeUnmount() {
    this.clearExportPoller();
  },
  methods: {
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    defaultStartDate() {
      const d = new Date();
      d.setDate(1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    async fetchCategories() {
      try {
        const response = await axios.get("/v1/category");
        this.categories = response.data || [];
      } catch (error) {
        console.error("Failed to load categories", error);
      }
    },
    async generateReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/salesreport", {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            categoryid: this.filters.categoryid || undefined,
            group_by: this.filters.groupBy === "itemwise" ? "itemwise" : "itemwise",
          },
        });
        this.rawRows = response.data || [];
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load sales report",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        categoryid: "",
        groupBy: "itemwise",
      };
      this.rawRows = [];
      this.generateReport();
    },
    clearExportPoller() {
      if (this.exportPoller) {
        window.clearInterval(this.exportPoller);
        this.exportPoller = null;
      }
    },
    async startExport(exportFormat) {
      this.clearExportPoller();
      try {
        const response = await axios.post("/v1/salesreport/export", null, {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            categoryid: this.filters.categoryid || undefined,
            group_by: this.filters.groupBy,
            export_format: exportFormat,
          },
        });
        this.exportJob = {
          ...this.exportJob,
          ...response.data,
          error: "",
        };
        this.pollExportJob(this.exportJob.job_id);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to start export",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    async pollExportJob(jobId) {
      await this.fetchExportJob(jobId);
      this.exportPoller = window.setInterval(async () => {
        const finished = await this.fetchExportJob(jobId);
        if (finished) {
          this.clearExportPoller();
        }
      }, 2000);
    },
    async fetchExportJob(jobId) {
      try {
        const response = await axios.get(`/v1/salesreport/export/${jobId}`);
        this.exportJob = {
          ...this.exportJob,
          ...response.data,
        };
        return ["completed", "failed"].includes(this.exportJob.status);
      } catch (error) {
        this.clearExportPoller();
        this.exportJob = {
          ...this.exportJob,
          status: "failed",
          error: error.response?.data?.detail || error.message,
        };
        return true;
      }
    },
    async downloadExport() {
      if (!this.exportJob.job_id) return;
      try {
        const response = await axios.get(`/v1/salesreport/export/${this.exportJob.job_id}/download`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = this.exportJob.filename || `sales_report.${this.exportJob.export_format === "pdf" ? "pdf" : "xlsx"}`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Download failed",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    buildCategoryRows(sourceRows) {
      const rows = [];
      const categories = new Map();
      sourceRows.forEach((row) => {
        const key = row.categoryname || "Uncategorized";
        if (!categories.has(key)) categories.set(key, []);
        categories.get(key).push(row);
      });

      Array.from(categories.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((categoryName) => {
          const categoryRows = categories.get(categoryName) || [];
          rows.push({
            rowkey: `header-${categoryName}`,
            rowtype: "category_header",
            categoryname: categoryName,
          });

          let qty = 0;
          let amount = 0;
          let taxamount = 0;
          let total = 0;

          categoryRows.forEach((row) => {
            rows.push({
              ...row,
              rowtype: "item",
            });
            qty += Number(row.quantity || 0);
            amount += Number(row.amount || 0);
            taxamount += Number(row.taxamount || 0);
            total += Number(row.total || 0);
          });

          rows.push({
            rowkey: `total-${categoryName}`,
            rowtype: "category_total",
            categoryname: "",
            itemname: `Total - ${categoryName}`,
            quantity: qty,
            saleprice: null,
            amount,
            taxpercent: null,
            taxamount,
            total,
          });
        });

      return rows;
    },
    buildItemRows(sourceRows) {
      const rows = [];
      const items = new Map();
      sourceRows.forEach((row) => {
        const key = row.itemname || "Item";
        if (!items.has(key)) items.set(key, []);
        items.get(key).push(row);
      });

      Array.from(items.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((itemName) => {
          const itemRows = items.get(itemName) || [];
          const firstRow = itemRows[0] || {};
          rows.push({
            rowkey: `header-${itemName}`,
            rowtype: "category_header",
            categoryname: firstRow.categoryname || "",
            itemname: itemName,
          });

          let qty = 0;
          let amount = 0;
          let taxamount = 0;
          let total = 0;

          itemRows.forEach((row) => {
            rows.push({
              ...row,
              rowtype: "item",
            });
            qty += Number(row.quantity || 0);
            amount += Number(row.amount || 0);
            taxamount += Number(row.taxamount || 0);
            total += Number(row.total || 0);
          });

          rows.push({
            rowkey: `total-${itemName}`,
            rowtype: "category_total",
            categoryname: "",
            itemname: `Total - ${itemName}`,
            quantity: qty,
            saleprice: null,
            amount,
            taxpercent: null,
            taxamount,
            total,
          });
        });

      return rows;
    },
    rowSerialNumber(record, index) {
      if (record.rowtype === "category_header" || record.rowtype === "category_total") {
        return "";
      }
      if (this.filters.groupBy !== "billwise") {
        return this.displayRows.filter((row, rowIndex) => row.rowtype === "item" && rowIndex <= index).length;
      }
      return index + 1;
    },
    rowClassName(record) {
      if (record.rowtype === "category_header") return "category-header-row";
      if (record.rowtype === "category_total") return "category-total-row";
      return "";
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    displayValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
  },
};
</script>

<style scoped>
.report-card {
  border: none;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
}
.report-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}
.report-header-bar h5 {
  margin: 0 0 4px;
  color: #0f172a;
}
.report-header-bar p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.export-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.report-toolbar {
  background: linear-gradient(135deg, #f8fbff 0%, #fff7ed 100%);
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(170px, 1fr)) auto;
  gap: 12px;
  align-items: start;
}
.form-group label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 5px;
}
.button-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  align-self: start;
  padding-top: 22px;
}
.button-group .btn {
  width: auto;
  min-width: 86px;
  height: 44px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}
.button-group .btn-submit {
  min-width: 112px;
}
.export-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
}
.export-status-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.export-status strong {
  color: #0f172a;
}
.export-status span {
  color: #64748b;
  font-size: 13px;
}
.status-completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.status-failed {
  border-color: #fecaca;
  background: #fef2f2;
}
.btn-download {
  background: #111827;
  color: #fff;
  border: none;
}
.summary-strip {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.summary-strip div {
  min-width: 160px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #111827;
  color: #fff;
}
.summary-strip span {
  display: block;
  font-size: 11px;
  opacity: 0.75;
}
.summary-strip strong {
  font-size: 18px;
}
.money-text,
.qty-text {
  font-family: "Courier New", monospace;
  font-weight: 700;
}
.category-pill {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e8f2ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}
.group-header-text {
  font-weight: 800;
  color: #0f172a;
}
.group-total-text {
  font-weight: 800;
  color: #0f172a;
}
:deep(.ant-table-thead > tr > th),
:deep(.ant-table-tbody > tr > td) {
  vertical-align: top;
}
:deep(.category-header-row > td) {
  background: #eff6ff !important;
  font-weight: 700;
}
:deep(.category-total-row > td) {
  background: #f8fafc !important;
  font-weight: 700;
}
.billwise-sheet {
  width: 100%;
  border: 1px solid #cbd5e1;
  color: #111827;
  font-size: 12px;
}
.billwise-message {
  padding: 24px;
  text-align: center;
  color: #64748b;
}
.billwise-bill {
  page-break-inside: avoid;
}
.billwise-bill + .billwise-bill {
  border-top: 1px solid #334155;
}
.billwise-bill-header {
  display: grid;
  grid-template-columns: 38px 130px minmax(190px, 1fr) minmax(260px, 1.8fr);
  gap: 10px;
  align-items: start;
  padding: 7px 10px 5px;
  font-weight: 700;
}
.bill-index {
  text-align: center;
}
.bill-customer {
  text-transform: uppercase;
}
.bill-address {
  text-align: right;
}
.billwise-items {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
.billwise-items th {
  padding: 4px 7px;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.billwise-items td {
  padding: 4px 7px;
  vertical-align: top;
}
.billwise-items th:nth-child(1) { width: 10%; }
.billwise-items th:nth-child(2) { width: 10%; }
.billwise-items th:nth-child(3) { width: 24%; }
.billwise-items th:nth-child(4) { width: 7%; }
.billwise-items th:nth-child(5) { width: 9%; }
.billwise-items th:nth-child(6) { width: 10%; }
.billwise-items th:nth-child(7) { width: 7%; }
.billwise-items th:nth-child(8) { width: 9%; }
.billwise-items th:nth-child(9) { width: 7%; }
.billwise-items th:nth-child(10) { width: 10%; }
.billwise-items .numeric {
  text-align: right;
  white-space: nowrap;
}
.billwise-items tfoot td {
  border-top: 1px solid #cbd5e1;
  padding-top: 5px;
  padding-bottom: 5px;
  font-weight: 700;
}
.billwise-items tfoot td:first-child {
  text-align: right;
}
@media (max-width: 992px) {
  .report-header-bar {
    flex-direction: column;
  }
  .export-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .button-group {
    padding-top: 0;
    justify-content: flex-start;
  }
  .export-status {
    flex-direction: column;
    align-items: flex-start;
  }
  .billwise-sheet {
    overflow-x: auto;
  }
  .billwise-bill {
    min-width: 900px;
  }
}
</style>
